import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  IconFileText,
  IconLayoutGrid,
  IconMenu4,
  IconMessageCircle,
  IconSearch,
  IconSquareRoundedPlus,
  IconStack2,
} from "@tabler/icons-react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { HapticEffect } from "../../interfaces";
import { iosGenerateHaptic } from "../../tools/ios";
import Skeleton from "../skeleton/Skeleton";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import {
  assistantConversationListTitle,
  conversationListTitle,
} from "../../domains/gpt/transcript";
import { useConversationEngine } from "../../domains/assistant/useConversationEngine";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useApiInfiniteQuery, useApiQuery } from "../../services/api";
import { liveActiveTurn } from "../../../../api/src/shared/domain/gpt-turn";
import { cn } from "../../lib/utils";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Logo } from "../ui/Logo";
import { useHasUnseenVisualAbstracts } from "../../features/visual-abstract/unseenVisualAbstracts";
import { ConversationMenu } from "./ConversationMenu";
import {
  CONVERSATION_SHARE_FLAG,
  useShareConversation,
  useUnshareConversation,
} from "../../domains/gpt/hooks/useShareConversation";
import {
  useDeleteConversation,
  useRenameConversation,
} from "../../domains/gpt/hooks/useManageConversation";
import { GPT_HEADER_ROW } from "./layout";

// One row of the RECENTS list. The two histories (general JuisciGPT chats and
// Chat-with-Paper sessions) are merged into a single reverse-chronological
// feed, so the row keeps the discriminant that decides its icon and its link.
type RecentItem =
  | {
      kind: "gpt";
      sortDate: string;
      conversationId: string;
      title: string;
      // Null until the agent names the thread — the row then labels itself with
      // the opening question, which is NOT a title the rename field should
      // prefill with.
      storedTitle: string | null;
      // Rides the row because the share analytics event carries it, and only the
      // list response knows the transcript.
      questionCount: number;
      // Offers Stop sharing.
      isShared: boolean;
    }
  | {
      kind: "cwyop";
      sortDate: string;
      articleId: string;
      articleSlug: string;
      title: string;
    };

// Placeholder title widths for the loading RECENTS list. Uneven on purpose:
// conversation titles are one truncated line of prose, and a column of
// identical bars reads as a table, not as a list of chats.
const RECENTS_SKELETON_WIDTHS = [
  "72%",
  "88%",
  "55%",
  "80%",
  "64%",
  "84%",
] as const;

/**
 * The JuisciGPT conversation history, as a panel that sits UNDER the page: the
 * burger slides the whole JuisciGPT surface aside to reveal it. It is always
 * mounted so the reveal has something to reveal — `isOpen` only drives the
 * transform — but its queries stay idle until it is opened.
 */
export function GPTHistoryPanel({
  isOpen,
  roomId,
  onNewConversation,
  onConversationDeleted,
}: {
  isOpen: boolean;
  roomId: string | null;
  /** Closes the panel and resets the landing to a blank conversation. */
  onNewConversation: () => void;
  /**
   * A row's conversation was deleted. The list revalidates itself, so this is
   * only for the surface BEHIND the drawer: the conversation page has to leave
   * the thread it is showing when that thread is the one that just went.
   */
  // Every id the delete took: a continuation and its legacy origin go together.
  onConversationDeleted: (conversationIds: string[]) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  // Same gate as the per-turn Share button: off → the row menu offers Rename and
  // Delete only.
  const isShareEnabled = useFeatureIsOn(CONVERSATION_SHARE_FLAG);
  const shareConversation = useShareConversation();
  const unshareConversation = useUnshareConversation();
  const hasUnseenVisualAbstracts = useHasUnseenVisualAbstracts();
  const renameConversation = useRenameConversation();
  const deleteConversation = useDeleteConversation();
  const { gptConversationPageLink, chatWithPaperConversationLink } =
    usePageLinks(roomId);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const search = debouncedQuery === "" ? null : debouncedQuery;
  // The history lists the engine that answers new questions.
  const engine = useConversationEngine();
  const conversationsInput = {
    query:
      search === null
        ? { limit: 20, sort: "newest" as const }
        : { limit: 20, q: search, sort: "newest" as const },
  };

  // Both feeds are narrowed server-side to what the rows display. Idle
  // (`false`) while the panel is closed: the landing is the JuisciGPT home, and
  // most visits never open the history.
  const conversationsQuery = useApiInfiniteQuery(
    "/webapp/private/conversations",
    isOpen && engine === "legacy" ? conversationsInput : false
  );
  const assistantQuery = useApiInfiniteQuery(
    "/assistant/conversations",
    isOpen && engine === "assistant" ? conversationsInput : false
  );
  const cwyopQuery = useApiQuery(
    "/webapp/private/chat-with-paper/sessions",
    isOpen
      ? {
          query:
            search === null
              ? { limit: 20, offset: 0 }
              : { limit: 20, offset: 0, q: search },
        }
      : false
  );

  const conversations =
    conversationsQuery.state === "success" ? conversationsQuery.docs : [];
  const assistantConversations =
    assistantQuery.state === "success" ? assistantQuery.docs : [];
  const cwyopSessions =
    cwyopQuery.state === "success" ? cwyopQuery.body.docs : [];
  const isLoading =
    (isOpen && engine === "pending") ||
    (conversationsQuery.state === "pending" && conversationsQuery.isFetching) ||
    (assistantQuery.state === "pending" && assistantQuery.isFetching) ||
    (cwyopQuery.state === "pending" && cwyopQuery.isFetching);

  const recentItems = useMemo((): RecentItem[] => {
    const items: RecentItem[] = [];

    const now = Date.now();
    for (const conversation of conversations) {
      // A turn still being written counts as a question asked.
      const activeTurn = liveActiveTurn(conversation, now);
      items.push({
        kind: "gpt",
        sortDate: conversation.createdAt,
        conversationId: conversation.id,
        title: conversationListTitle(conversation, now),
        storedTitle: conversation.title,
        questionCount:
          conversation.messages.filter((message) => message.role === "user")
            .length + (activeTurn === null ? 0 : 1),
        isShared: conversation.isShared,
      });
    }

    // Sorted like its list, which dates a legacy row by its creation.
    for (const conversation of assistantConversations) {
      items.push({
        kind: "gpt",
        sortDate: conversation.sortDate,
        conversationId: conversation.id,
        title: assistantConversationListTitle(conversation),
        storedTitle: conversation.title,
        questionCount: conversation.questionCount,
        isShared: conversation.isShared,
      });
    }

    for (const session of cwyopSessions) {
      items.push({
        kind: "cwyop",
        sortDate: session.updatedAt,
        articleId: session.articleId,
        articleSlug: session.articleSlug,
        title: session.articleTitle,
      });
    }

    items.sort(
      (a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime()
    );
    return items;
  }, [conversations, assistantConversations, cwyopSessions]);

  return (
    <>
      {/* The page slides aside as a rounded card, and its two left corner
          notches fall outside both the card and the panel (which only spans
          `w-4/5`). This lays the drawer's own surface under them, so the
          corners read as cream rather than as wedges of the app shell. It is a
          separate layer from the panel because the panel travels by its own
          width, and a full-bleed element sliding the same way would fly its
          content in from a screen away. */}
      <div
        className={`fixed inset-0 z-1 bg-ds-cream transition-opacity duration-300 ease-in-out ${
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <div
        // 80% of the viewport is the design's 314/393, capped so the panel
        // stays a drawer rather than half a desktop window. The page's push
        // offset (GPTNewChatPage) is the same `min()`, so the two line up.
        // z-1, BELOW the page's z-2: the card has to pass over the drawer, not
        // under it.
        className={`fixed top-0 left-0 z-1 flex h-dvh w-4/5 max-w-96 flex-col transition-transform duration-300 ease-in-out will-change-transform ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        // Exactly the offset `ScrollableLayout` gives the page header, so the
        // drawer's header row and the page's are the same box at the same
        // height: the logo lands on the burger's optical line, and the burger
        // stays visible beside it while the page is only pushed aside.
        style={{ paddingTop: "var(--safe-area-top)" }}
      >
        {/* The page's own header row, reproduced: same class, same top offset,
            so the logo sits on the burger's line rather than under it. Its px-5
            is also the panel's gutter — the same one the navigation row, the
            search field, the RECENTS rows and the bottom button sit on, so
            every block in the drawer shares one left edge. */}
        <div className={GPT_HEADER_ROW}>
          <Logo variant="no-tagline" tone="color" className="h-8" />
        </div>

        <hr className="m-0 h-px w-full border-none bg-ds-slate-100" />

        {/* The drawer's navigation rows: everything else under them is a
            conversation. They sit above the search field because they are
            destinations, not history entries — a poster or a collection is not
            a chat, and the field below searches chats only. */}
        <Link
          to="/visual-abstracts"
          className="flex items-center gap-2 px-5 pt-5 pb-2.5 no-underline"
        >
          {/* The dot rides the ICON, not the row: it marks the destination the
              way the JuisciGPT tab is marked, and a row-wide badge would float
              at the drawer's right edge, far from the thing it qualifies. */}
          <Badge show={hasUnseenVisualAbstracts} position="tr">
            <IconLayoutGrid
              className="size-4 shrink-0 text-ds-black"
              stroke={1.25}
            />
          </Badge>
          <span className="font-dm-sans text-ui-body font-medium text-ds-black">
            {t("Visual abstracts")}
          </span>
        </Link>

        {/* One tap to the collections the reader made, which they show on a
            visit; without it they sit at the foot of the profile page. */}
        <Link
          to="/profile/playlist"
          className="flex items-center gap-2 px-5 pt-2.5 pb-5 no-underline"
        >
          <IconStack2 className="size-4 shrink-0 text-ds-black" stroke={1.25} />
          <span className="font-dm-sans text-ui-body font-medium text-ds-black">
            {t("Collections I created")}
          </span>
        </Link>

        <hr className="m-0 h-px w-full border-none bg-ds-slate-100" />

        {/* The field heads the list it filters, with air above so it reads as
            the top of that block rather than as the tail of the divider. */}
        <div className="px-5 pt-4 pb-1">
          <div className="flex items-center gap-2 rounded-lg bg-ds-black/6 px-3.5 py-2.5">
            <IconSearch className="size-4 shrink-0 text-ds-slate-500" />
            <input
              type="text"
              value={query}
              placeholder={t("Search a chat")}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              className="w-full bg-transparent font-dm-sans text-ui-note font-medium text-ds-black outline-none placeholder:text-ds-slate-500"
            />
          </div>
        </div>

        {/* px-5: the same gutter as the search field above and the button
            below, so the RECENTS rows share both edges with them. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 pt-3 pb-2">
          <p className="m-0 font-dm-sans text-ui-note font-bold text-ds-slate-600 uppercase">
            {t("Recents")}
          </p>
          {isLoading ? (
            <div className="flex flex-col gap-1 pt-2">
              {RECENTS_SKELETON_WIDTHS.map((width, index) => (
                <div key={index} className="flex items-center gap-1 py-2">
                  <Skeleton $variant="rounded" width={16} height={16} />
                  <Skeleton $variant="rounded" width={width} height={12} />
                </div>
              ))}
            </div>
          ) : recentItems.length === 0 ? (
            <p className="mt-4 mb-0 font-dm-sans text-ui-note font-medium text-ds-slate-500">
              {search === null
                ? t(
                    "Oops, looks like you haven't opened a conversation with JuisciGPT yet."
                  )
                : t("No conversation matches your search.")}
            </p>
          ) : (
            <div className="flex flex-col gap-1 pt-2">
              {recentItems.map((item) => (
                <div
                  key={
                    item.kind === "gpt"
                      ? item.conversationId
                      : `cwyop-${item.articleSlug}`
                  }
                  className="group relative flex items-center gap-1"
                >
                  <Link
                    to={
                      item.kind === "gpt"
                        ? {
                            pathname: gptConversationPageLink(
                              item.conversationId
                            ),
                            // The row hands the destination its own exit
                            // animation: the conversation paints under a still-open
                            // drawer and plays it closed. Chat-with-Paper carries no
                            // drawer, so its rows say nothing — signalling an
                            // entrance that cannot play would be a lie.
                            state: { historyArrival: "dismissing" },
                          }
                        : chatWithPaperConversationLink(
                            item.articleSlug,
                            item.articleId
                          )
                    }
                    className="flex min-w-0 flex-1 items-center gap-1 rounded-lg py-2 no-underline"
                  >
                    {item.kind === "gpt" ? (
                      <IconMessageCircle
                        className="size-4 shrink-0 text-ds-black"
                        stroke={1.25}
                      />
                    ) : (
                      <IconFileText
                        className="size-4 shrink-0 text-ds-black"
                        stroke={1.25}
                      />
                    )}
                    <span className="truncate font-dm-sans text-ui-body font-medium text-ds-black">
                      {item.title}
                    </span>
                  </Link>
                  {/* Only general threads: a Chat-with-Paper session is found
                      again by its paper, so renaming or deleting it means
                      nothing. A sibling of the link, never inside it — an anchor
                      may not hold a button.

                      The reveal is gated on `(hover: hover)`, not on `md` alone.
                      A tablet is a wide screen with no hover, and Tailwind's own
                      `group-hover` already compiles to that media query: with
                      the fade keyed to breakpoint instead, the trigger on an
                      iPad would stay at `opacity: 0` for the whole session while
                      staying fully clickable — a tap near the right edge of a
                      row would open an invisible menu instead of the
                      conversation. Where hover does not exist the trigger is
                      simply always visible. */}
                  {item.kind === "gpt" && (
                    <ConversationMenu
                      title={item.storedTitle}
                      onShare={
                        isShareEnabled
                          ? () => {
                              void shareConversation(
                                item.conversationId,
                                item.questionCount
                              );
                            }
                          : null
                      }
                      onUnshare={
                        isShareEnabled && item.isShared
                          ? () => {
                              void unshareConversation(item.conversationId);
                            }
                          : null
                      }
                      onDebug={null}
                      onRename={(title) =>
                        renameConversation(item.conversationId, title)
                      }
                      onDelete={async () => {
                        const deleted = await deleteConversation(
                          item.conversationId
                        );
                        if (deleted === null) return false;
                        onConversationDeleted(deleted);
                        return true;
                      }}
                      className="hidden transition-opacity md:flex [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:focus-within:opacity-100 [@media(hover:hover)]:data-[open=true]:opacity-100"
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 pt-2 pb-8">
          <Button
            variant="primary"
            size="md"
            type="button"
            className="w-full"
            leftIcon={
              <IconSquareRoundedPlus className="size-4" stroke={1.25} />
            }
            onClick={onNewConversation}
          >
            {t("New conversation")}
          </Button>
        </div>
      </div>
    </>
  );
}

// Finger travel before a touch commits to a direction. Below this it is still
// ambiguous — it could become the page's vertical scroll.
const SWIPE_ACTIVATION_PX = 12;
// Horizontal travel (from the touch's origin) that fires the drawer. Short
// enough to feel like a flick, long enough that a sloppy tap never toggles.
const SWIPE_COMMIT_PX = 64;

type SwipeGesture =
  | { phase: "idle" }
  | { phase: "deciding" | "horizontal"; startX: number; startY: number };

/**
 * True when the point sits inside a carousel that scrolls horizontally (the
 * pre-prompt scroller, the source chips, the conversation tabs). A sideways
 * drag started there belongs to that carousel, never to the drawer.
 */
function isInHorizontalScroller(clientX: number, clientY: number): boolean {
  let element = document.elementFromPoint(clientX, clientY);
  while (element !== null) {
    const { overflowX } = window.getComputedStyle(element);
    if (
      (overflowX === "auto" || overflowX === "scroll") &&
      element.scrollWidth > element.clientWidth
    )
      return true;
    element = element.parentElement;
  }
  return false;
}

/**
 * Swipe right anywhere on the JuisciGPT surface to reveal the history, swipe
 * left to push it back. Listens on `window` rather than on a node because the
 * gesture spans BOTH surfaces the reveal moves — the page card while closed,
 * the drawer and its scrim while open — and those live in different subtrees.
 * Only the JuisciGPT routes ever mount it, and `enabled` turns it off on the
 * chrome variants that do not carry a drawer at all.
 */
function useGptHistorySwipe({
  enabled,
  isOpen,
  onOpen,
  onClose,
}: {
  enabled: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}): void {
  const gesture = useRef<SwipeGesture>({ phase: "idle" });

  useEffect(() => {
    if (!enabled) return;

    function handleTouchStart(event: TouchEvent) {
      const touch = event.touches[0];
      // A second finger means a pinch/zoom, not a drawer gesture.
      if (touch === undefined || event.touches.length > 1) {
        gesture.current = { phase: "idle" };
        return;
      }
      // Only the CLOSED page has carousels to protect. Once the drawer is open
      // the page behind it is inert under its scrim and the drawer itself is a
      // vertical list, so nothing there can legitimately claim a sideways drag
      // — and the guard would wrongly claim it anyway: a `overflow-y: auto`
      // list (RECENTS) computes `overflow-x: auto`, so the slightest horizontal
      // overflow inside it would swallow the closing swipe.
      if (!isOpen && isInHorizontalScroller(touch.clientX, touch.clientY)) {
        gesture.current = { phase: "idle" };
        return;
      }
      gesture.current = {
        phase: "deciding",
        startX: touch.clientX,
        startY: touch.clientY,
      };
    }

    function handleTouchMove(event: TouchEvent) {
      const current = gesture.current;
      if (current.phase === "idle") return;
      const touch = event.touches[0];
      if (touch === undefined) return;
      const dx = touch.clientX - current.startX;
      const dy = touch.clientY - current.startY;

      if (current.phase === "deciding") {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_ACTIVATION_PX) return;
        // Commit: anything not predominantly horizontal is the page scrolling,
        // and must stay the page scrolling — the browser keeps it.
        if (Math.abs(dx) <= Math.abs(dy)) {
          gesture.current = { phase: "idle" };
          return;
        }
        gesture.current = { ...current, phase: "horizontal" };
      }

      if (Math.abs(dx) < SWIPE_COMMIT_PX) return;
      // Fire once and disarm: without this, a long drag past the threshold
      // would keep re-firing on every frame of the same gesture.
      gesture.current = { phase: "idle" };
      if (dx > 0 && !isOpen) onOpen();
      if (dx < 0 && isOpen) onClose();
    }

    function handleTouchEnd() {
      gesture.current = { phase: "idle" };
    }

    // Passive throughout: the gesture never cancels the browser's own scroll —
    // it only reads the finger, and a committed drag is horizontal while the
    // page scrolls vertically.
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    window.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [enabled, isOpen, onOpen, onClose]);
}

/**
 * How the drawer stands when a surface carrying it mounts, and how it got
 * there. Travels as router state (`historyArrival`) because it describes ONE
 * navigation, not a preference: it must not survive the reload that drops
 * router state, or the surface would replay an entrance nobody asked for.
 *
 *  - `closed`: the ordinary arrival. Nothing moves.
 *  - `revealing`: the destination IS the conversation list — Back out of a
 *    conversation, the profile's "See all". The surface paints shut, then
 *    plays the reveal, so landing on the list looks like opening the drawer.
 *  - `dismissing`: we arrived BY picking a row in an open drawer. The surface
 *    paints with the drawer still open, then plays it closed, so the page the
 *    row opened rides in underneath instead of replacing it in one cut.
 *
 * The schema is exported alongside the type because every consumer reads this
 * off router state, and a second hand-written union in each page's schema is a
 * copy that drifts.
 */
export const gptHistoryArrivalSchema = z.enum([
  "closed",
  "revealing",
  "dismissing",
]);

export type GptHistoryArrival = z.infer<typeof gptHistoryArrivalSchema>;

/**
 * The push-aside reveal that uncovers `GPTHistoryPanel`: the JuisciGPT surface
 * slides sideways at full size, as a rounded card.
 * Returns the open state plus the exact props that turn a `ScrollableLayout`
 * into that card — every JuisciGPT surface that carries the drawer (the landing
 * and the conversation) reveals it identically, down to the easing.
 *
 * `swipeEnabled` is the caller's answer to "does this page currently show the
 * drawer?" — the landing hides it in conversation mode, the conversation hides
 * it while the feed-customization flow takes the page over, and a gesture that
 * moved a surface nobody rendered would be a dead toggle.
 */
export function useGptHistoryReveal({
  arrival,
  swipeEnabled,
}: {
  arrival: GptHistoryArrival;
  swipeEnabled: boolean;
}): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** Spread onto the layout's `containerClassName`. */
  containerClassName: string;
  /** Spread onto the layout's `containerStyle`. */
  containerStyle: CSSProperties;
  /** Merge into the layout's scroller `style` — freezes the page behind the panel. */
  scrollLockStyle: CSSProperties;
} {
  const [isOpen, setOpen] = useState(arrival === "dismissing");
  // An arrival belongs to a NAVIGATION, not to a mount. A surface that holds
  // the drawer above the route param it renders — the conversation page does,
  // so the drawer survives switching chats — is not remounted when the next
  // row is picked, and keying this on `arrival` alone would leave the drawer
  // standing open over the conversation it just opened.
  const { key: navigationKey } = useLocation();

  // Playing an arrival is a transition, not a keyframe: paint the state we
  // arrived in, then flip to the resting one and let the SAME CSS transition
  // the burger drives carry us between them — one description of the movement
  // rather than a second copy that drifts from it.
  //
  // Two frames, not one. A flip in the commit that lands us here collapses
  // into that first paint, and a transition with nothing to travel from
  // renders as exactly the instant cut this exists to remove: the first frame
  // gets the arrival state on screen, the second moves off it.
  useEffect(() => {
    if (arrival === "closed") return;
    let settle = 0;
    const paint = requestAnimationFrame(() => {
      settle = requestAnimationFrame(() => {
        setOpen(arrival === "revealing");
      });
    });
    return () => {
      cancelAnimationFrame(paint);
      cancelAnimationFrame(settle);
    };
  }, [arrival, navigationKey]);

  const open = useCallback(() => {
    iosGenerateHaptic(HapticEffect.SELECTION);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useGptHistorySwipe({
    enabled: swipeEnabled,
    isOpen,
    onOpen: open,
    onClose: close,
  });

  return {
    isOpen,
    open,
    close,
    // z-2, ABOVE the panel's z-1: the card passes over the drawer, not under it.
    containerClassName: "relative z-2",
    containerStyle: {
      background: "var(--color-ds-cream)",
      // The offset is exactly the panel's own width (`w-4/5 max-w-96`), so the
      // card lands flush against it — the page uncovers the drawer and no more,
      // on a phone and on a desktop alike.
      transform: isOpen ? "translateX(min(80%, 24rem))" : "translateX(0)",
      borderRadius: isOpen ? "var(--radius-3xl)" : "0px",
      // Clip to the radius — without it the corners stay square and the card
      // reads as a rectangle with rounded paint on top. This also catches the
      // composer, which is `fixed` inside a transformed ancestor and so belongs
      // to this box.
      overflow: "hidden",
      boxShadow: isOpen ? "0px 10px 40px rgba(11, 17, 16, 0.18)" : "none",
      transition:
        "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.3s ease, box-shadow 0.3s ease",
      willChange: "transform",
      // The scrim re-enables its own pointer events, so it stays the only thing
      // the pushed-aside page will answer a tap on.
      pointerEvents: isOpen ? "none" : "auto",
    },
    scrollLockStyle: isOpen ? { overflowY: "hidden", touchAction: "none" } : {},
  };
}

/** What `useGptHistoryReveal` hands back, inferred rather than redeclared. */
export type GptHistoryReveal = ReturnType<typeof useGptHistoryReveal>;

/**
 * Dims the pushed-aside page and takes the tap that closes the panel. Rides
 * INSIDE the transformed container, so it travels with the page instead of
 * veiling the drawer too.
 */
export function GPTHistoryScrim({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}): React.ReactElement {
  return (
    <div
      className={cn(
        "absolute top-0 left-0 z-25 box-border h-dvh w-full cursor-pointer transition-all duration-300 ease-in-out will-change-[opacity]",
        isOpen
          ? "pointer-events-auto bg-black/15 opacity-100"
          : "pointer-events-none bg-black/0 opacity-0"
      )}
      onClick={onClose}
    />
  );
}

/** The burger that opens the drawer, identical on every surface carrying it. */
export function GPTHistoryButton({
  onClick,
  className,
}: {
  onClick: () => void;
  className: string | null;
}): React.ReactElement {
  const { t } = useTranslation();
  // The same dot the drawer's Visual abstracts row wears, repeated on the
  // handle that opens the drawer: the row is behind a panel nobody opens
  // speculatively, so a mark only inside it announces nothing.
  const hasUnseenVisualAbstracts = useHasUnseenVisualAbstracts();

  return (
    <button
      type="button"
      aria-label={t("Conversation history")}
      className={cn(
        "flex size-9 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-ds-black",
        className
      )}
      onClick={onClick}
    >
      <Badge show={hasUnseenVisualAbstracts} position="tr">
        <IconMenu4 className="size-6" stroke={1.5} />
      </Badge>
    </button>
  );
}
